import mapboxgl from "mapbox-gl";
import { CONFIG } from "../config";
import { organisationService } from "../services/organisationService";
import { dateUtils } from "./dateUtils";
import { temperatureUtils } from "./temperatureUtils";
import hotspot from "../hotspot.png";

//mapbox access token
mapboxgl.accessToken = CONFIG.mapboxAccessToken;

export const mapUtils = {
  // Create trackers map for dashboard
  createTrackersMap: async (mapRef, devices) => {
    try {
      let features = devices.map((device) => {
        let data = JSON.parse(device.last_data);

        if (!data) return null;

        if (!data.latitude || !data.longitude) return null;

        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [data.longitude, data.latitude],
          },
          properties: {
            title: device.name,
            description: device.description,
          },
        };
      });

      //remove null features
      features = features.filter((feature) => feature !== null);

      if (features.length === 0) return;

      let geoJsonObject = {
        type: "FeatureCollection",
        features: features,
      };

      // If map already exists, just update the data source and bounds
      if (mapRef.current) {
        if (mapRef.current.getSource("trackers")) {
          mapRef.current.getSource("trackers").setData(geoJsonObject);
        }

        //get the bounds of all the markers
        let bounds = new mapboxgl.LngLatBounds();
        features.forEach((feature) => {
          bounds.extend(feature.geometry.coordinates);
        });

        //fit the map to the bounds
        if (!bounds.isEmpty()) {
          mapRef.current.fitBounds(bounds, {
            padding: 50,
            animated: false,
            duration: 0,
            essential: true,
          });
        }
        return;
      }

      let initialLongLat = [0, 0];

      mapRef.current = new mapboxgl.Map({
        container: "map",
        style: CONFIG.mapStyle,
        center: initialLongLat,
        zoom: 16,
        animated: false,
        interactive: true,
        attributionControl: false,
      });

      mapRef.current.on("load", function () {
        // Add the GeoJSON source with clustering enabled
        mapRef.current.addSource("trackers", {
          type: "geojson",
          data: geoJsonObject,
          cluster: true,
          clusterMaxZoom: 14, // Max zoom to cluster points on
          clusterRadius: 50, // Radius of each cluster when clustering points (defaults to 50)
        });

        // Add a layer for clustered points
        mapRef.current.addLayer({
          id: "clusters",
          type: "circle",
          source: "trackers",
          filter: ["has", "point_count"],
          paint: {
            "circle-color": [
              "step",
              ["get", "point_count"],
              "#51bbd6",
              100,
              "#f1f075",
              750,
              "#f28cb1",
            ],
            "circle-radius": [
              "step",
              ["get", "point_count"],
              20,
              100,
              30,
              750,
              40,
            ],
          },
        });

        // Add a layer for cluster counts
        mapRef.current.addLayer({
          id: "cluster-count",
          type: "symbol",
          source: "trackers",
          filter: ["has", "point_count"],
          layout: {
            "text-field": "{point_count_abbreviated}",
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12,
          },
        });

        // Add a layer for unclustered points
        mapRef.current.addLayer({
          id: "unclustered-point",
          type: "circle",
          source: "trackers",
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-color": "#007cbf",
            "circle-radius": 8,
            "circle-stroke-width": 1,
            "circle-stroke-color": "#fff",
          },
        });

        // inspect a cluster on click
        mapRef.current.on("click", "clusters", (e) => {
          const features = mapRef.current.queryRenderedFeatures(e.point, {
            layers: ["clusters"],
          });
          const clusterId = features[0].properties.cluster_id;
          mapRef.current
            .getSource("trackers")
            .getClusterExpansionZoom(clusterId, (err, zoom) => {
              if (err) return;

              mapRef.current.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom,
              });
            });
        });

        // When a click event occurs on a feature in the unclustered-point layer, open a popup at
        // the location of the feature, with description HTML from its properties.
        mapRef.current.on("click", "unclustered-point", (e) => {
          const coordinates = e.features[0].geometry.coordinates.slice();
          const { title, description } = e.features[0].properties;

          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
          }

          new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(`<h3>${title}</h3><p>${description}</p>`)
            .addTo(mapRef.current);
        });

        mapRef.current.on("mouseenter", "clusters", () => {
          mapRef.current.getCanvas().style.cursor = "pointer";
        });
        mapRef.current.on("mouseleave", "clusters", () => {
          mapRef.current.getCanvas().style.cursor = "";
        });
        mapRef.current.on("mouseenter", "unclustered-point", () => {
          mapRef.current.getCanvas().style.cursor = "pointer";
        });
        mapRef.current.on("mouseleave", "unclustered-point", () => {
          mapRef.current.getCanvas().style.cursor = "";
        });

        //get the bounds of all the markers
        let bounds = new mapboxgl.LngLatBounds();
        features.forEach((feature) => {
          bounds.extend(feature.geometry.coordinates);
        });

        //fit the map to the bounds
        if (!bounds.isEmpty()) {
          mapRef.current.fitBounds(bounds, {
            padding: 50,
            animated: false,
            duration: 0,
            essential: true,
          });
        }
      });
    } catch (error) {
      console.error("Error creating trackers map:", error);
    }
  },

  // Create device detail map
  createMap: async (mapRef, history, deviceData) => {
    try {
      if (!mapRef.current === null) return;

      let initialLongLat = [0, 0];

      if (history && history.length > 0) {
        //parse the last data object in history
        let data = JSON.parse(history[history.length - 1].data);
        initialLongLat = [data.longitude, data.latitude];
      }

      mapRef.current = new mapboxgl.Map({
        container: "map",
        style: CONFIG.mapStyle,
        center: initialLongLat,
        zoom: 16,
        animated: false,
      });

      // Wait for the map to load before adding any sources or layers
      mapRef.current.on("load", async function () {
        //add polyline
        await mapUtils.addPolyline(history, mapRef.current, deviceData);

        //add markers for all the history
        await mapUtils.addHistoryMarkers(history, mapRef.current);

        //add current location
        mapUtils.addCurrentLocation(history, mapRef.current, deviceData);

        //add logo and other images
        mapUtils.addImages(mapRef.current);

        //add geofences
        if (deviceData && deviceData.organisation_id) {
          await mapUtils.addGeofences(
            { id: deviceData.organisation_id },
            mapRef.current,
          );
        }

        //create bounds of history
        let bounds = new mapboxgl.LngLatBounds();

        //add each point to the bounds
        if (history && history.length > 0) {
          history.forEach((item) => {
            let data = JSON.parse(item.data);
            //check latitude and longitude are not 0
            if (data.latitude !== 0 && data.longitude !== 0) {
              //check if not empty string
              if (data.latitude !== "" && data.longitude !== "") {
                //check not null
                if (data.latitude !== null && data.longitude !== null) {
                  bounds.extend(
                    mapUtils.minMaxLngLat([data.longitude, data.latitude]),
                  );
                }
              }
            }
          });

          //check if bounds are empty
          if (!bounds.isEmpty()) {
            //set the map bounds
            mapRef.current.fitBounds(bounds, {
              padding: 50,
              maxZoom: 16,
              duration: 0,
              animated: false,
            });
          }
        }
      });
    } catch (error) {
      console.error("Error creating device map:", error);
    }
  },

  // Helper functions for map operations
  createGeoJSONCircleFeature: (
    center,
    radiusInMeters,
    points,
    geocode,
    time,
    index,
  ) => {
    if (!points) points = 64;
    if (!radiusInMeters) radiusInMeters = 5;

    var coords = {
      latitude: center[1],
      longitude: center[0],
    };

    //convert meters to km
    var km = radiusInMeters / 1000;

    var ret = [];
    var distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
    var distanceY = km / 110.574;

    var theta, x, y;
    for (var i = 0; i < points; i++) {
      theta = (i / points) * (2 * Math.PI);
      x = distanceX * Math.cos(theta);
      y = distanceY * Math.sin(theta);

      ret.push([coords.longitude + x, coords.latitude + y]);
    }
    ret.push(ret[0]);

    const feature = {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [ret],
      },
      properties: {
        title: geocode,
        time: time,
        index: index,
        longitude: center[0],
        latitude: center[1],
      },
    };

    return feature;
  },

  getMatch: async (coordinates, profile = "driving") => {
    if (coordinates.length < 3) {
      return coordinates;
    }

    // Create the query
    const query = await fetch(
      `https://api.mapbox.com/directions/v5/mapbox/${profile}/${coordinates}?access_token=${CONFIG.mapboxAccessToken}&geometries=geojson`,
      { method: "GET" },
    );
    const response = await query.json();

    // Handle errors
    if (response.code !== "Ok") {
      console.log("response error", response);
      return coordinates;
    }
    return response.routes[0].geometry.coordinates;
  },

  getCleanedPolyline: async (coordinates, profile) => {
    try {
      // split coordinates into 25 point chunks, if last chunk is less than 100 points, add it to the previous chunk
      let chunks = [];
      let chunk = [];
      for (let i = 0; i < coordinates.length; i++) {
        if (chunk.length < 25) {
          chunk.push(coordinates[i]);
        } else {
          chunks.push(chunk);
          chunk = [];
          chunk.push(coordinates[i]);
        }
      }
      if (chunk.length > 0) {
        chunks.push(chunk);
      }

      // get the cleaned line for each chunk
      let cleanedLines = [];
      for (let i = 0; i < chunks.length; i++) {
        let cleanedLine = await mapUtils.getMatch(chunks[i].join(";"), profile);
        cleanedLines.push(cleanedLine);
      }

      // reduce the array of arrays to a single array
      cleanedLines = cleanedLines.reduce((acc, val) => acc.concat(val), []);

      return cleanedLines;
    } catch (e) {
      console.log("error cleaning line", e);
      return coordinates;
    }
  },

  getGeofences: async (selectedOrganisationID) => {
    try {
      const geofences = await organisationService.getOrganisationGeofences(
        selectedOrganisationID,
      );
      return geofences;
    } catch (error) {
      console.log(error);
    }

    return null;
  },

  // Add current location with pulsing dot
  addCurrentLocation: (history, mapRef, deviceData) => {
    try {
      const size = 200;

      const pulsingDot = {
        width: size,
        height: size,
        data: new Uint8Array(size * size * 4),

        // When the layer is added to the map,
        // get the rendering context for the map canvas.
        onAdd: function () {
          const canvas = document.createElement("canvas");
          canvas.width = this.width;
          canvas.height = this.height;
          this.context = canvas.getContext("2d");
        },

        // Call once before every frame where the icon will be used.
        render: function () {
          const duration = 3000;
          const t = (performance.now() % duration) / duration;

          const radius = (size / 3) * 0.3;
          const outerRadius = (size / 3) * 0.7 * t + radius;
          const context = this.context;

          // Draw the outer circle.
          context.clearRect(0, 0, this.width, this.height);
          context.beginPath();
          context.arc(
            this.width / 2,
            this.height / 2,
            outerRadius,
            0,
            Math.PI * 2,
          );
          context.fillStyle = `rgba(0, 208, 159, ${1 - t})`;
          context.fill();

          // Draw the inner circle.
          context.beginPath();
          context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
          context.fillStyle = "rgba(0, 208, 159)";
          context.strokeStyle = "#004d40";
          context.lineWidth = 2 + 4 * (1 - t);
          context.fill();
          context.stroke();

          // Update this image's data with data from the canvas.
          this.data = context.getImageData(0, 0, this.width, this.height).data;

          // Continuously repaint the map, resulting
          // in the smooth animation of the dot.
          mapRef.triggerRepaint();

          // Return `true` to let the map know that the image was updated.
          return true;
        },
      };

      if (history.length > 0) {
        // get last history object data and parse it
        let data = JSON.parse(history[history.length - 1].data);

        if (!mapRef.getSource("dot-point")) {
          mapRef.addSource("dot-point", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [
                {
                  type: "Feature",
                  geometry: {
                    type: "Point",
                    coordinates: [data.longitude, data.latitude],
                  },
                  properties: {
                    title: data.geocode,
                    data: data,
                  },
                },
              ],
            },
          });

          mapRef.addImage("pulsing-dot", pulsingDot, { pixelRatio: 2 });

          mapRef.addLayer({
            id: "layer-with-pulsing-dot",
            type: "symbol",
            source: "dot-point",
            layout: {
              "icon-image": "pulsing-dot",
            },
          });

          //onclick of dot-point, fly to location and show popup
          mapRef.on("click", "layer-with-pulsing-dot", function (e) {
            mapRef.flyTo({
              center: e.features[0].geometry.coordinates,
              maxDuration: 2000,
              essential: true,
              animated: false,
            });

            //parse json data
            let data = JSON.parse(e.features[0].properties.data);

            //for each key in data, write html with key and value
            let html = "";

            for (const [key, value] of Object.entries(data)) {
              //if key is latitude or longitude or batteryMv or accuracy or geocode, skip
              if (
                key === "latitude" ||
                key === "longitude" ||
                key === "batteryMv" ||
                key === "accuracy" ||
                key === "geocode" ||
                key === "wifi_macs" ||
                key === "uplink" ||
                key === "downlink" ||
                key === "dont_log_frame" ||
                key === "voltageMv"
              ) {
                continue;
              }
              if (key === "temperature") {
                if (value === null) {
                  continue;
                }
                html += `<p><strong>Temperature:</strong> ${temperatureUtils.formatTemperature(
                  value,
                )}</p>`;
                continue;
              }

              if (key === "humidity") {
                if (value === null) {
                  continue;
                }
                html += `<p><strong>Humidity:</strong> ${value}%</p>`;
                continue;
              }

              if (key === "co2") {
                if (value === null) {
                  continue;
                }
                html += `<p><strong>CO2:</strong> ${value}ppm</p>`;
                continue;
              }

              if (key === "button_pressed") {
                if (value === null) {
                  continue;
                }
                html += `<p><strong>Button Pressed:</strong> ${
                  value === 1 ? "Yes" : "No"
                }</p>`;
                continue;
              }

              html += `<p><strong>${key}:</strong> ${value}</p>`;
            }

            new mapboxgl.Popup()
              .setLngLat(e.features[0].geometry.coordinates)
              .setHTML(
                "<h3>" +
                  dateUtils.formatDate(e.features[0].properties.time) +
                  "</h3><p><strong>Location:</strong><br /> " +
                  e.features[0].properties.title +
                  "</p>" +
                  html,
              )
              .addTo(mapRef);
          });
        } else {
          //update dot-point source
          mapRef.getSource("dot-point").setData({
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: [data.longitude, data.latitude],
                },
                properties: {
                  title: data.geocode,
                  data: data,
                },
              },
            ],
          });
        }
      }
    } catch (error) {
      console.log(error);
    }
  },

  // Add geofences to map
  addGeofences: async (organisation, mapRef) => {
    try {
      if (!mapRef) return;

      let geofences = await mapUtils.getGeofences(organisation.id);

      // if geofences object is empty, return
      if (!geofences || !geofences.length) return;

      function createGeoJSONCircle(center, radiusInMeters, points, geofence) {
        if (!points) points = 64;

        var coords = {
          latitude: center[1],
          longitude: center[0],
        };

        //convert meters to km
        var km = radiusInMeters / 1000;

        var ret = [];
        var distanceX =
          km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
        var distanceY = km / 110.574;

        var theta, x, y;
        for (var i = 0; i < points; i++) {
          theta = (i / points) * (2 * Math.PI);
          x = distanceX * Math.cos(theta);
          y = distanceY * Math.sin(theta);

          ret.push([coords.longitude + x, coords.latitude + y]);
        }
        ret.push(ret[0]);

        const feature = {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [ret],
          },
          properties: {
            name: geofence.name,
            radius: geofence.radius,
            longitude: geofence.longitude,
            latitude: geofence.latitude,
          },
        };

        return feature;
      }

      let features = {};

      if (geofences && geofences.length > 0) {
        features = {
          type: "FeatureCollection",
          features: geofences.map((item) => {
            return createGeoJSONCircle(
              [item.longitude, item.latitude],
              item.radius,
              64,
              item,
            );
          }),
        };
      }

      if (!mapRef.getSource("geofences")) {
        //add a source for tag history markers
        mapRef.addSource("geofences", {
          type: "geojson",
          data: features,
        });

        //add a layer for tag history circles use fill to paint the circle
        mapRef.addLayer({
          id: "geofences-layer",
          type: "fill",
          source: "geofences",
          paint: {
            "fill-color": "#4ecca3",
            "fill-opacity": 0.5,
            //border 1px green
            "fill-outline-color": "#004d40",
          },
        });
      } else {
        mapRef.getSource("geofences").setData(features);
      }
    } catch (error) {
      console.error(error);
    }
  },

  // Add history markers to map
  addHistoryMarkers: async (history, mapRef) => {
    if (!mapRef) return;

    //get features from history.data after json parsing
    let features = [];

    history.forEach((item, index) => {
      let data = item.data;
      //json parse data
      if (typeof data === "string") {
        data = JSON.parse(data);
      }

      //return coordinates
      features.push(
        mapUtils.createGeoJSONCircleFeature(
          [data.longitude, data.latitude],
          30,
          64,
          data.geocode,
          item.time_created,
          index,
        ),
      );
    });

    //create a geojson object from the features array
    features = {
      type: "FeatureCollection",
      features: features,
    };

    // if features.features.length === 1. empty the array
    if (features.features.length === 1) {
      features.features = [];
    }

    if (!mapRef.getSource("device-history-markers")) {
      //add a source for device history markers
      mapRef.addSource("device-history-markers", {
        type: "geojson",
        data: features,
      });

      //add a layer for device history markers to use standard marker pin
      mapRef.addLayer({
        id: "device-history-markers-layer",
        type: "fill",
        source: "device-history-markers",
        paint: {
          "fill-color": "#4ecca3",
          "fill-opacity": 0.5,
          "fill-outline-color": "#004d40",
          //hide overflows
          "fill-antialias": false,
        },
        //hide overflows
        layout: {
          visibility: "visible",
        },

        filter: ["==", "$type", "Polygon"],
      });

      //when a device history marker is clicked, fly to that location and show a popup
      mapRef.on("click", "device-history-markers-layer", function (e) {
        mapRef.flyTo({
          center: [
            e.features[0].properties.longitude,
            e.features[0].properties.latitude,
          ],
          maxDuration: 2000,
          essential: true,
          animated: true,
        });

        new mapboxgl.Popup()
          .setLngLat([
            e.features[0].properties.longitude,
            e.features[0].properties.latitude,
          ])
          .setHTML(
            "<h3>" +
              dateUtils.formatDate(e.features[0].properties.time) +
              "</h3><p><strong>Location:</strong><br /> " +
              e.features[0].properties.title +
              "</p>",
          )
          .addTo(mapRef);
      });
    }
  },

  // Add polyline for device history
  addPolyline: async (history, mapRef, deviceProfile = null) => {
    try {
      //check history length is greater than 0
      if (history && history.length > 0) {
        //create coords array for history.data items using time_created to order

        let coords = history.map((item) => {
          let data = item.data;
          //json parse data
          if (typeof data === "string") {
            data = JSON.parse(data);
          }
          //return coordinates if not empty
          if (data.latitude && data.longitude) {
            if (data.latitude !== 0 && data.longitude !== 0) {
              return [data.longitude, data.latitude];
            } else {
              return null;
            }
          }
          return null;
        });

        //filter out null values
        coords = coords.filter((item) => item !== null);

        let polyLineGeometry = coords;

        //if route_matching_type is not none, get cleaned polyline
        if (
          deviceProfile &&
          deviceProfile.tracker &&
          deviceProfile.tracker.route_matching_type !== "none"
        ) {
          polyLineGeometry = await mapUtils.getCleanedPolyline(
            coords,
            deviceProfile.tracker.route_matching_type,
          );
        }

        if (!mapRef.getSource("history")) {
          // add a source for the device's location history
          mapRef.addSource("history", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [
                {
                  type: "Feature",
                  geometry: {
                    type: "LineString",
                    coordinates: polyLineGeometry,
                  },
                },
              ],
            },
          });

          mapRef.addLayer({
            type: "line",
            source: "history",
            id: "line-background",
            paint: {
              "line-color": "#00D09F",
              "line-width": 3,
              "line-opacity": 1,
            },
          });
        } else {
          //if source already exists, update the source data
          mapRef.getSource("history").setData({
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: {
                  type: "LineString",
                  coordinates: polyLineGeometry,
                },
              },
            ],
          });
        }
      }
    } catch (err) {
      console.log(err);
    }
  },

  // Add images to map
  addImages: (map) => {
    //add image for location history marker
    map.loadImage(
      "http://manage.trackpac.io/device-icon.png",
      function (error, image) {
        if (error) {
          //try adding it from localhost:3000 instead
          map.loadImage(
            "http://localhost:3000/device-icon.png",
            function (error, image) {
              if (error) throw error;
            },
          );
        }
        map.addImage("marker-15", image);
      },
    );

    //add image for hotspot-icon from local source
    map.loadImage(hotspot, (error, image) => {
      if (error) throw error;
      map.addImage("hotspot-icon", image);
    });
  },

  // Ensure longitude and latitude are within valid ranges
  minMaxLngLat: (lngLat) => {
    const minLng = -180;
    const maxLng = 180;
    const minLat = -85;
    const maxLat = 85;

    const lng = Math.max(minLng, Math.min(maxLng, lngLat[0]));
    const lat = Math.max(minLat, Math.min(maxLat, lngLat[1]));

    return [lng, lat];
  },
};
